import React, { FormEvent, useContext, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Text } from 'styles';
import { BackButton } from 'components/back-button';
import { Alert, Button, Notification } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { useUser } from 'hooks/use-user.hook';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { Page } from 'styles';
import { UPDATE_PREFERENCE_MUTATION } from './language-preference.api';
import { StyledSelect } from '../../components/styled-select/styled-select.style';

type Lang = 'en' | 'es';
const getCopy = copyFor('settings.userSettings.languagePreference');
const { settings } = routes();

export const LanguagePreference = () => {
  const history = useHistory();
  const { addNotification } = useContext(Notification.Context);
  const [mutate] = useMutation(UPDATE_PREFERENCE_MUTATION);

  const { data: user, loading: userLoading } = useUser();
  const [language, setLanguage] = useState<Lang>(
    user?.languageShortCode as Lang
  );

  const handleLanguageChange = selectedLanguage => {
    if (selectedLanguage === getCopy('es')) setLanguage('es');
    else setLanguage('en');
  };

  const [{ error: mutationError, loading: mutationLoading }, submit] =
    useAsyncFn(async () => {
      await mutate({
        variables: {
          id: user?.id,
          input: {
            languageShortCode: language,
          },
        },
      });
      addNotification({
        displayTime: NOTIFICATION_DISPLAY_TIME,
        message: getCopy('toast'),
        variant: 'success',
      });
    }, [mutate, language, user, addNotification]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const userLanguage = user?.languageShortCode;
    if (userLanguage && language !== userLanguage) {
      window.Localize?.setLanguage(language);
      submit();
    }
  };

  const selectOptions = [{ text: getCopy('en') }, { text: getCopy('es') }];

  return (
    <>
      <BackButton
        previousRoute={settings}
        onClick={() => history.push(settings)}
      />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text
          tag="h1"
          textStyle="heading800"
          margin={{ bottom: '16px' }}
          data-testid="new-movers"
        >
          {getCopy('subHeading')}
        </Text>
        <Text>{getCopy('description')}</Text>
        <form onSubmit={handleSubmit}>
          {mutationError && (
            <Alert margin={{ top: '32px' }}>{getCopy('error')}</Alert>
          )}
          <StyledSelect
            margin="32px 0px 48px 0px"
            label={getCopy('label')}
            name={getCopy('heading')}
            onChange={handleLanguageChange}
            options={selectOptions}
            value={getCopy(`${language}`)}
            loading={mutationLoading || userLoading}
            withEmptyPlaceholder={false}
            minHeight="0px"
          />
          <Button
            disabled={!language || user?.languageShortCode === language}
            fullWidth
            loading={mutationLoading || userLoading}
            type="submit"
          >
            {getCopy('button')}
          </Button>
        </form>
      </Page>
    </>
  );
};
